import type { FC } from 'react';
import type React from 'react';
import type { ContextOption } from '@components/menu';
import { ContextMenu } from '@components/menu';
import { useEntityStore } from '@ContractBuilder/store';
import type { Attachment } from '@ContractBuilder/types';
import { canChange } from '@helpers/canChange';
import { useModalDispatch } from '@hooks/useModalProvider';
import { useLanguage } from '@root/src/language';
import { MODALS } from '@src/constants';

interface AttachmentMenuProps {
  className?: string;
  isOpen: boolean;
  onClose: () => void;
  onOpen: () => void;
  attachment: Attachment;
}

export const InlineAttachmentMenu: FC<AttachmentMenuProps> = ({ className, isOpen, onClose, onOpen, attachment }) => {
  const { showModal } = useModalDispatch();
  const isDisabled = useEntityStore(({ submission }) => !canChange(submission?.status));

  const { getContent } = useLanguage({ prefix: 'naming.mrc' });

  const eventPropagationTrap = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
  };

  const options: ContextOption[] = [
    {
      title: getContent('contextMenus.attachment.rename'),
      onClick: () =>
        showModal(MODALS.ATTACHMENT_RENAME, {
          attachmentId: attachment.id,
          attachmentName: attachment?.name,
          attachmentFileName: attachment?.fileName,
        }),
      icon: 'edit',
    },
    {
      title: getContent('contextMenus.attachment.remove'),
      onClick: () =>
        showModal(MODALS.CONFIRM_DELETE, {
          id: attachment.id,
          title: attachment?.name,
          type: getContent('attachment').toLowerCase(),
        }),
      icon: 'trashcan',
    },
  ];

  return (
    <div className={className} onClick={eventPropagationTrap}>
      <ContextMenu
        dataTestId={`attachment-menu`}
        disabled={isDisabled}
        isOpen={isOpen}
        onClose={onClose}
        onOpen={onOpen}
        options={options}
      />
    </div>
  );
};
